import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, ShieldCheck, Ban, MailCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { OwnerLeadsPanel } from "@/components/OwnerLeadsPanel";

const ROLE_LABEL = {
  agency: "Agency", nonprofit: "Nonprofit", operator: "Operator",
  property_owner: "Property Owner", investor: "Investor", other: "Other",
};

const STATUS_CHIP = {
  new: "bg-amber-100 text-amber-800",
  contacted: "bg-blue-100 text-blue-800",
  approved: "bg-emerald-100 text-emerald-800",
  provisioned: "bg-emerald-200 text-emerald-900",
  declined: "bg-slate-200 text-slate-700",
  closed: "bg-slate-100 text-slate-600",
};

const SOURCE_LABEL = {
  landing_form: "Landing form",
  google_signin_blocked: "Google sign-in blocked",
};

export default function AccessRequests() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all"); // all | pending | approved | declined
  const [busyId, setBusyId] = useState("");

  const load = () => api.get("/access-requests").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return rows;
    if (filter === "pending") return rows.filter(r => !r.status || r.status === "new" || r.status === "contacted");
    if (filter === "approved") return rows.filter(r => r.status === "approved" || r.status === "provisioned");
    if (filter === "declined") return rows.filter(r => r.status === "declined" || r.status === "closed");
    return rows;
  }, [rows, filter]);

  const setStatus = async (id, status) => {
    await api.put(`/access-requests/${id}`, { status });
    toast.success(`Marked ${status}`);
    load();
  };

  const approve = async (r) => {
    if (!r.email) { toast.error("This request has no email — cannot invite."); return; }
    const role = window.prompt(`Approve ${r.email} as which role? Type "admin" or "analyst".`, "analyst");
    if (!role) return;
    if (!["admin", "analyst"].includes(role.trim())) { toast.error("Role must be admin or analyst."); return; }
    setBusyId(r.id);
    try {
      await api.post(`/access-requests/${r.id}/approve`, { role: role.trim() });
      toast.success("Invite email sent — link expires in 72 hours.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed.");
    } finally { setBusyId(""); }
  };

  const decline = async (r) => {
    if (!window.confirm(`Decline the request from ${r.email || r.name}? No email will be sent.`)) return;
    setBusyId(r.id);
    try {
      await api.post(`/access-requests/${r.id}/decline`, {});
      toast.success("Declined.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Decline failed.");
    } finally { setBusyId(""); }
  };

  const counts = useMemo(() => ({
    all: rows.length,
    pending: rows.filter(r => !r.status || r.status === "new" || r.status === "contacted").length,
    approved: rows.filter(r => r.status === "approved" || r.status === "provisioned").length,
    declined: rows.filter(r => r.status === "declined" || r.status === "closed").length,
  }), [rows]);

  return (
    <div className="space-y-6" data-testid="access-requests-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-2"><Users className="w-3.5 h-3.5" /> Inbound</div>
        <h1 className="text-3xl font-bold">Access Requests</h1>
        <p className="text-sm text-slate-500 mt-1">Submissions from the public landing page and blocked Google sign-ins. Approve to send a 72-hour, single-use setup link so the invitee can pick their own password.</p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="access-filter-bar">
        {[
          { k: "all", label: "All" },
          { k: "pending", label: "Pending" },
          { k: "approved", label: "Approved" },
          { k: "declined", label: "Declined / closed" },
        ].map(f => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            className={`px-3 py-1.5 text-xs rounded-full border ${filter === f.k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}
            data-testid={`filter-${f.k}`}
          >
            {f.label} <span className="ml-1 opacity-70">{counts[f.k]}</span>
          </button>
        ))}
      </div>

      <Card className="p-0 border-slate-200 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-6 text-sm text-slate-500 italic">No requests in this view.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-white text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Received</th>
                <th className="text-left px-3 py-2">Name / Organization</th>
                <th className="text-left px-3 py-2">Role</th>
                <th className="text-left px-3 py-2">Source</th>
                <th className="text-left px-3 py-2">Looking for</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const isGoogleBlocked = r.source === "google_signin_blocked";
                const isTerminal = r.status === "provisioned" || r.status === "declined" || r.status === "closed" || r.status === "approved";
                return (
                  <tr key={r.id} className={`border-b border-slate-100 ${isGoogleBlocked ? "bg-amber-50/40" : ""}`} data-testid={`access-row-${r.id}`}>
                    <td className="px-3 py-2 text-xs text-slate-500 font-mono">{(r.created_at || "").slice(0,10)}</td>
                    <td className="px-3 py-2">
                      <div className="font-semibold">{r.name || <span className="text-slate-400 italic">unnamed</span>}</div>
                      <div className="text-xs text-slate-500">{r.organization ? `${r.organization} · ` : ""}{r.email || <span className="italic">no email</span>}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">{ROLE_LABEL[r.role_type] || r.role_type || "—"}</td>
                    <td className="px-3 py-2 text-xs">
                      <span className={`inline-block px-2 py-0.5 rounded ${isGoogleBlocked ? "bg-amber-200 text-amber-900" : "bg-slate-100 text-slate-700"}`}>{SOURCE_LABEL[r.source] || r.source || "—"}</span>
                    </td>
                    <td className="px-3 py-2 text-xs max-w-md">{r.looking_for || r.message || "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${STATUS_CHIP[r.status] || STATUS_CHIP.new}`}>{r.status || "new"}</span>
                      {r.status === "approved" && r.invite_expires_at && (
                        <div className="text-[10px] text-slate-500 mt-0.5" title={r.invite_expires_at}>invite expires {r.invite_expires_at.slice(0,10)}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {!isTerminal && r.email && (
                        <Button size="sm" className="mr-1 bg-emerald-700 hover:bg-emerald-800 text-white"
                                disabled={busyId === r.id}
                                onClick={() => approve(r)} data-testid={`approve-${r.id}`}>
                          <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Approve
                        </Button>
                      )}
                      {!isTerminal && (
                        <Button size="sm" variant="outline" className="mr-1"
                                disabled={busyId === r.id}
                                onClick={() => decline(r)} data-testid={`decline-${r.id}`}>
                          <Ban className="w-3.5 h-3.5 mr-1" /> Decline
                        </Button>
                      )}
                      {!isTerminal && r.status !== "contacted" && (
                        <Button size="sm" variant="outline" className="mr-1"
                                onClick={() => setStatus(r.id, "contacted")} data-testid={`mark-contacted-${r.id}`}>
                          <MailCheck className="w-3.5 h-3.5 mr-1" /> Contacted
                        </Button>
                      )}
                      {r.status === "provisioned" && (
                        <span className="text-[11px] text-emerald-800 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> user created</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      <OwnerLeadsPanel />
    </div>
  );
}
